// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getProductsForProductInfoList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import Icon from "@insite/mobius/Icon/Icon";
import { LoadingSpinner } from "@insite/mobius/index";
import Link from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import { recentlyPurchasedMenuStyles } from "@rugby/App/Components/RecentlyPurchasedMenu/RecentlyPurchasedMenu.Styles";
import { XngMenusContext } from "@rugby/App/Components/XngMenu";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    products: getProductsForProductInfoList(state, "headerRecentlyPurchased"),
    viewAllLink: getPageLinkByPageType(state, "OrderHistoryPage")?.url || "",
});


interface RecentlyPurchasedMenuProps {
    menuId: string
}


type Props = ReturnType<typeof mapStateToProps> & RecentlyPurchasedMenuProps;

const RecentlyPurchasedMenu: React.FC<Props> = ({ products, viewAllLink, menuId }) => {
    const {hideMenu} = React.useContext(XngMenusContext);
    let statusMessage;
    if (typeof products === "undefined") {
        statusMessage = <LoadingSpinner size={22} color="text.main" />;
    } else if (products.length === 0) {
        statusMessage = (
            <Typography
                variant="h4"
                color="secondary"
                css={css`
                    margin: 0;
                `}
            >
                {translate("No Recent Purchases Found")}
            </Typography>
        );
    }

    const handleMenuClose = () => {
        hideMenu(menuId)
    }

    return (
        <StyledWrapper {...recentlyPurchasedMenuStyles.container} data-test-selector="recently_purchased_menu">
            <Icon onClick={handleMenuClose} {...recentlyPurchasedMenuStyles.closeButton} />
            <StyledWrapper {...recentlyPurchasedMenuStyles.headerWrapper}>
                <Typography {...recentlyPurchasedMenuStyles.titleText}>{translate("Recent Purchases")}</Typography>
                <Link {...recentlyPurchasedMenuStyles.viewAllLink} href={viewAllLink}>
                    {translate("View All Recent Purchases")}
                </Link>
            </StyledWrapper>
            {statusMessage && (
                <StyledWrapper {...recentlyPurchasedMenuStyles.statusContainer}>{statusMessage}</StyledWrapper>
            )}
            <StyledWrapper
                css={css`
                    max-width: 1200px;
                    min-width: 1200px;
                `}
            >
                {createWidgetElement("Common/ProductCarousel", widgetProps)}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetProps = {
    id: "headerRecentlyPurchased",
    fields: {
        carouselType: "headerRecentlyPurchased",
        title: " ",
        showAddToList: true,
        showAddToCart: true,
        showImage: true,
        showBrand: true,
        showTitle: true,
        showPartNumbers: true,
        showPrice: true,
        showInventory: true,
    },
};

export default connect(mapStateToProps)(RecentlyPurchasedMenu);
